import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment, Box } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField, Typography } from '@orijinworks/frontend-commons';
import { AutocompleteSkeleton, AutocompleteOption } from '../../AttorneyModal/sections';
import {
  membersAutocompleteSx,
  searchIcon,
} from '../../AttorneyModal/AttorneyModal.styles';
import { useAutocompleteStyles } from './MemberAutocomplete.styles';
import { useMemberAutocomplete } from './useMemberAutocomplete';
import { useMemberAutocompleteDropdownFooter } from './useMemberAutocompleteDropdownFooter';

export const DEFAULT_NO_OPTIONS_TEXT = 'No User(s) found!';

const MemberAutocomplete = ({
  memberOptions = [],
  selectedMembers = [],
  inputValue,
  onMembersChange,
  onSearchChange,
  isMaxMembersReached = false,
  isMemberListLoading = false,
  showDisplayNamesOnly = false,
  filterOptions,
  getOptionLabel,
  isOptionEqualToValue,
  getOptionDisabled,
  renderOptionEndAdornment,
  multiple = true,
  freeSolo,
  disabled = false,
  label = 'Members',
  placeholder = 'Search',
  helperText,
  rootClassName = 'member-autocomplete-root',
  dropdownFooterLeftText,
  noOptionsText = DEFAULT_NO_OPTIONS_TEXT,
}) => {
  const classes = useAutocompleteStyles();
  const [internalInputValue, setInternalInputValue] = useState('');
  const resolvedInputValue =
    inputValue !== undefined ? inputValue : internalInputValue;

  const { handleChange, handleInputChange, handleKeyDown, getOptionProps, popperProps } =
    useMemberAutocomplete({
      memberOptions,
      selectedMembers,
      onMembersChange,
      onSearchChange,
      showDisplayNamesOnly,
    });
  const { applyFooter } = useMemberAutocompleteDropdownFooter({
    rootClassName,
    memberOptions,
    footerLeftText: dropdownFooterLeftText,
  });

  const isFreeSolo = freeSolo !== undefined ? freeSolo : !multiple;
  const handleAutocompleteInputChange = useCallback(
    (event, nextValue, reason) => {
      if (inputValue === undefined && reason === 'input') {
        setInternalInputValue(nextValue);
      }
      handleInputChange(event, nextValue, reason);
    },
    [handleInputChange, inputValue],
  );

  // Only show empty-state copy after the user has typed a search query.
  const resolvedNoOptionsText = useMemo(() => {
    if (isMemberListLoading) {
      return null;
    }
    if (!String(resolvedInputValue || '').trim()) {
      return null;
    }
    return (
      <Typography variant="body1" className={classes.noOptionsText}>
        {noOptionsText}
      </Typography>
    );
  }, [
    classes.noOptionsText,
    isMemberListLoading,
    resolvedInputValue,
    noOptionsText,
  ]);

  return (
    <Box className={rootClassName}>
      <Autocomplete
        multiple={multiple}
        options={memberOptions}
        value={selectedMembers}
        inputValue={inputValue !== undefined ? inputValue : undefined}
        disabled={disabled || isMaxMembersReached}
        disableClearable={true}
        disableCloseOnSelect={false}
        disablePortal={false}
        openOnFocus
        freeSolo={isFreeSolo}
        noOptionsText={resolvedNoOptionsText}
        getOptionLabel={getOptionLabel}
        getOptionDisabled={getOptionDisabled}
        filterOptions={filterOptions}
        isOptionEqualToValue={isOptionEqualToValue}
        renderTags={() => null}
        onChange={handleChange}
        onInputChange={handleAutocompleteInputChange}
        onOpen={applyFooter}
        className={classes.autocompleteRoot}
        renderInput={(params) => {
          const {
            InputLabelProps,
            InputProps,
            inputProps,
            ...textFieldParams
          } = params;

          return (
            <TextField
              {...textFieldParams}
              label={label}
              placeholder={placeholder}
              variant="outlined"
              helperText={helperText}
              slotProps={{
                inputLabel: {
                  ...InputLabelProps,
                  shrink: true,
                },
                input: {
                  ...InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={searchIcon} />
                    </InputAdornment>
                  ),
                  endAdornment: InputProps?.endAdornment || null,
                },
                htmlInput: {
                  ...inputProps,
                  onKeyDown: (event) =>
                    handleKeyDown(event, inputProps?.onKeyDown),
                },
              }}
              sx={membersAutocompleteSx}
            />
          );
        }}
        PopperProps={popperProps}
        renderOption={(option) => {
          const optionProps = getOptionProps(option);
          const endAdornment = renderOptionEndAdornment
            ? renderOptionEndAdornment(option)
            : null;
          return (
            <AutocompleteOption
              {...optionProps}
              endAdornment={endAdornment}
            />
          );
        }}
        ListboxComponent={({ children, ...other }) => (
          <div {...other}>
            {isMemberListLoading ? <AutocompleteSkeleton /> : children}
          </div>
        )}
      />
    </Box>
  );
};

export default MemberAutocomplete;

MemberAutocomplete.propTypes = {
  memberOptions: PropTypes.array,
  selectedMembers: PropTypes.array,
  inputValue: PropTypes.string,
  onMembersChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  isMaxMembersReached: PropTypes.bool,
  isMemberListLoading: PropTypes.bool,
  showDisplayNamesOnly: PropTypes.bool,
  filterOptions: PropTypes.func,
  getOptionLabel: PropTypes.func,
  isOptionEqualToValue: PropTypes.func,
  getOptionDisabled: PropTypes.func,
  renderOptionEndAdornment: PropTypes.func,
  multiple: PropTypes.bool,
  freeSolo: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  helperText: PropTypes.node,
  rootClassName: PropTypes.string,
  dropdownFooterLeftText: PropTypes.string,
  noOptionsText: PropTypes.string,
};

MemberAutocomplete.defaultProps = {
  memberOptions: [],
  selectedMembers: [],
  inputValue: undefined,
  onMembersChange: undefined,
  onSearchChange: undefined,
  isMaxMembersReached: false,
  isMemberListLoading: false,
  showDisplayNamesOnly: false,
  filterOptions: undefined,
  getOptionLabel: undefined,
  isOptionEqualToValue: undefined,
  getOptionDisabled: undefined,
  renderOptionEndAdornment: undefined,
  multiple: true,
  freeSolo: undefined,
  disabled: false,
  label: 'Members',
  placeholder: 'Search',
  helperText: undefined,
  rootClassName: 'member-autocomplete-root',
  dropdownFooterLeftText: undefined,
  noOptionsText: DEFAULT_NO_OPTIONS_TEXT,
};
